import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { api } from "@/lib/api";
import { ScoreReport } from "@/components/ScoreReport";
import { PlanCard, SummaryStats, SessionsList, AccountSettings } from "./dashboard/DashboardParts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BrandLogo } from "@/components/BrandLogo";
import { GraduationCap, LogOut, Printer, Download } from "lucide-react";
import { SUBSCRIPTIONS_ENABLED } from "@/lib/featureFlags";

export default function Dashboard() {
  const { user, logout, changePassword, logoutAll, deleteAccount, resendVerification } = useAuth();
  const { entitlement, config, openPortal } = useBilling();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    api.get("/api/progress").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/sections");
  };

  const best = items && items.length ? Math.max(...items.map((i) => Math.round(i.score))) : null;
  const hasItems = items && items.length > 0;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">Training record</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline" data-testid="dashboard-user">
              {user?.name || user?.email}
            </span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn" className="text-slate-600">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 fade-up">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
              <GraduationCap className="w-3.5 h-3.5" />
              <span>My progress</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Your scores &amp; grades
            </h1>
            <p className="text-base text-slate-600 mt-2">
              Every saved simulator run is stored here with your score and grade.
            </p>
          </div>
          {hasItems && (
            <Button onClick={() => setShowReport(true)} data-testid="download-scores-btn" variant="outline" className="text-[#0047AB] border-[#0047AB]/30 mt-1">
              <Download className="w-4 h-4 mr-1.5" /> Download my scores (PDF)
            </Button>
          )}
        </div>

        {SUBSCRIPTIONS_ENABLED && user?.role !== "admin" && user?.role !== "instructor" && (
          <PlanCard entitlement={entitlement} config={config} onUpgrade={() => navigate("/pricing")} onManage={openPortal} />
        )}
        <SummaryStats count={items ? items.length : null} best={best} />
        <SessionsList items={items} />
        <AccountSettings user={user} changePassword={changePassword} logoutAll={logoutAll} deleteAccount={deleteAccount} resendVerification={resendVerification} />
      </main>

      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">My scores</DialogTitle>
          <DialogDescription className="sr-only">Printable report of your simulator scores and grades.</DialogDescription>
          <ScoreReport records={items} user={user} />
          <Button onClick={() => window.print()} data-testid="print-scores-btn" className="no-print w-full bg-[#0047AB] hover:bg-[#003380] text-white">
            <Printer className="w-4 h-4 mr-1.5" /> Print / Save as PDF
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
